import Button from '@/components/UI/Button';
import { errorToast, successToast } from '@/helpers';
import { _isNotBlank } from '@/littledash';
import InVivoError from '@/model/InVivoError';
import type { UserGroupCreatePayload, UserGroupRow } from '@/model/UserGroups.model';
import { ApiService, ResponseType } from '@/support/ApiService';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import type { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import UserGroupFields, { type UserGroupFormValues } from './UserGroupFields';
import { useEligibleMembers } from './useEligibleMembers';
import { fieldValidationMessage } from './UserGroupErrors';

interface CreateUserGroupModalProps {
  closeModal: () => void;
  onCreated: (group: UserGroupRow) => void;
}

/**
 * Creates a user group with its name, description and initial members. Members are sent with the
 * create so the group and its membership land in one transaction — a rejected member leaves no
 * half-made group behind.
 */
const CreateUserGroupModal: FC<CreateUserGroupModalProps> = ({ closeModal, onCreated }) => {
  const formMethods = useForm<UserGroupFormValues>({
    defaultValues: { name: '', description: '', memberApiIds: [] },
  });
  const members = useEligibleMembers();
  const {
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = formMethods;

  const onSubmit = async ({ name, description, memberApiIds }: UserGroupFormValues) => {
    const body: UserGroupCreatePayload = {
      name: name.trim(),
      description: _isNotBlank(description) ? description.trim() : null,
      member_api_ids: memberApiIds,
    };
    const response = await ApiService.call({
      endpoint: 'POST /api/v1/user-groups',
      path: undefined,
      body,
      options: { onError: { toast: false, capture: false, throw: false } },
    });

    if (response.type === ResponseType.Error) {
      const fieldMessage = fieldValidationMessage(response, 'name');
      const memberMessage = fieldValidationMessage(response, 'member_api_ids');
      if (_isNotBlank(fieldMessage)) {
        setError('name', { type: 'server', message: fieldMessage });
      } else if (_isNotBlank(memberMessage)) {
        errorToast(memberMessage);
      } else {
        errorToast('Could not create the group');
        ExceptionHandler.captureException(
          new InVivoError('Could not create user group', { cause: response.error, slug: 'user-group-create' })
        );
      }
      return;
    }

    const created = response.body;
    successToast(`Created group ${created.name}`);
    try {
      onCreated(created);
    } catch (error) {
      ExceptionHandler.captureException(
        new InVivoError('User group created but the list could not be refreshed', {
          cause: error,
          slug: 'user-group-created-callback',
        })
      );
    }
    closeModal();
  };

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="center mw7 ui-card"
        data-test-component="CreateUserGroupModal"
        data-test-element="container"
      >
        <div className="pa4" data-test-component="CreateUserGroupModal" data-test-element="header">
          <h3 className="normal f4" data-testid="title">
            Create user group
          </h3>
        </div>
        <div data-test-component="CreateUserGroupModal" data-test-element="body">
          <UserGroupFields submitting={isSubmitting} members={members} />
        </div>
        <div
          className="bt b--moon-gray bg-white pa3"
          data-test-component="CreateUserGroupModal"
          data-test-element="footer"
        >
          <Button submit className="mr2" disabled={isSubmitting || members.failed} testId="submit-button">
            {isSubmitting ? 'Creating…' : 'Create group'}
          </Button>
          <Button plain onClick={closeModal} disabled={isSubmitting} testId="cancel-button">
            Cancel
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default CreateUserGroupModal;
